import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { clsx } from "@regrello/core-utils";
import { RegrelloButton, RegrelloIcon, RegrelloLinkV2, RegrelloTooltip, RegrelloTypography } from "@regrello/ui-core";
import { parseAsString, useQueryState } from "nuqs";
import { useId, useState, useTransition } from "react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { ValidationRules } from "../../../constants/globalConstants";
import { RegrelloRestApiService } from "../../../services/RegrelloRestApiService";
import { RoutePaths } from "../../../ui2/app/routes/consts";
import { RegrelloControlledFormFieldText } from "../../../ui2/shared/molecules/formFields/controlled/RegrelloControlledFormFieldText";
import { RegrelloFormFieldCheckbox } from "../../../ui2/shared/molecules/formFields/RegrelloFormFieldCheckbox";
import { validatePassword } from "../../../utils/passwordRulesRegexUtils";
import { PasswordRuleList } from "./_internal/PasswordRuleList";
import { UnauthenticatedPageLayout } from "./_internal/UnauthenticatedPageLayout";

const Status = {
  NONE: "NONE",
  ERROR: "ERROR",
  SUCCESS: "SUCCESS",
} as const;
type Status = (typeof Status)[keyof typeof Status];

export function UnauthenticatedPasswordReset() {
  const tooltipId = useId();
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [isHintTooltipOpen, setIsHintTooltipOpen] = useState(true);
  const [status, setStatus] = useState<Status>(Status.NONE);
  const [isPending, startTransition] = useTransition();

  const [email] = useQueryState("email", parseAsString.withDefault(""));
  const [nonce] = useQueryState("nonce", parseAsString.withDefault(""));

  const form = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      password: "",
      passwordConfirm: "",
    },
  });

  const password = useWatch({ control: form.control, name: "password" });

  const resetPassword: SubmitHandler<{ password: string; passwordConfirm: string }> = (data) => {
    startTransition(async () => {
      try {
        const response = await RegrelloRestApiService.resetPassword(email, nonce, data.password);
        if (response.status >= 400) {
          setStatus(Status.ERROR);
        } else {
          setStatus(Status.SUCCESS);
        }
      } catch {
        setStatus(Status.ERROR);
        return;
      }
    });
  };

  if (!email || !nonce || status === Status.ERROR) {
    return (
      <UnauthenticatedPageLayout>
        <div className="flex flex-col justify-center items-center gap-4 w-100">
          <RegrelloIcon iconName="alert" intent="danger" size="large" />
          <RegrelloTypography variant="h2">{t`Password Reset Failed`}</RegrelloTypography>
          <RegrelloTypography className="text-center" variant="body">
            {t`Please try again or use another method to reset your password.`}
          </RegrelloTypography>
          <ul className="list-disc">
            <li>
              <Trans>
                Use the <strong>forgot password</strong> link on the <strong>sign in</strong> page.
              </Trans>
            </li>
            <li>{t`Contact your organization administrator.`}</li>
          </ul>
          <RegrelloLinkV2 className="font-normal" to={RoutePaths.LOGIN}>
            {t`Back to sign in`}
          </RegrelloLinkV2>
        </div>
      </UnauthenticatedPageLayout>
    );
  }

  if (status === Status.SUCCESS) {
    return (
      <UnauthenticatedPageLayout>
        <div className="flex flex-col justify-center items-center gap-4">
          <RegrelloIcon iconName="activity-approved" intent="success" size="large" />
          <RegrelloTypography variant="h2">{t`Password Reset Successful`}</RegrelloTypography>
          <RegrelloLinkV2 className="font-normal" to={RoutePaths.LOGIN}>
            {t`Back to sign in`}
          </RegrelloLinkV2>
        </div>
      </UnauthenticatedPageLayout>
    );
  }

  return (
    <UnauthenticatedPageLayout>
      <form
        className="flex flex-col justify-center items-center gap-4 w-80"
        onSubmit={form.handleSubmit(resetPassword)}
      >
        <RegrelloTypography className="mb-4" variant="h2">
          {t`Create your new password`}
        </RegrelloTypography>
        <RegrelloTooltip
          align="start"
          content={<PasswordRuleList password={password} />}
          contentProps={{
            id: tooltipId,
            role: "tooltip",
            style: {
              width: "var(--radix-tooltip-trigger-width)",
              maxHeight: "var(--radix-tooltip-content-available-height)",
            },
          }}
          open={isHintTooltipOpen}
          side="top"
          variant="popover"
        >
          <RegrelloControlledFormFieldText
            // eslint-disable-next-line lingui/no-unlocalized-strings
            aria-describedby={tooltipId}
            autoFocus={true}
            className="w-full"
            controllerProps={{
              control: form.control,
              name: "password",
              rules: {
                ...ValidationRules.REQUIRED,
                validate: (password: string) => {
                  const passwordRules = validatePassword(password);

                  if (!passwordRules.minLength) {
                    return t`Password must have at least 15 characters`;
                  }
                  if (!passwordRules.hasValidCharacterCombination) {
                    return t`Password must contain at least 3 of the 4 character types`;
                  }
                  if (!passwordRules.hasNoConsecutiveCharacters) {
                    return t`Password must not have more than 2 identical characters in a row`;
                  }
                  return true;
                },
              },
            }}
            isDefaultMarginsOmitted={true}
            onBlur={() => {
              setIsHintTooltipOpen(false);
            }}
            onFocus={() => {
              setIsHintTooltipOpen(true);
            }}
            placeholder={t`Create password`}
            type={isPasswordShown ? "text" : "password"}
          />
        </RegrelloTooltip>
        <RegrelloControlledFormFieldText
          className={clsx("w-full")}
          controllerProps={{
            control: form.control,
            name: "passwordConfirm",
            rules: {
              ...ValidationRules.REQUIRED,
              validate: (confirmPasswordValue: string) => {
                return confirmPasswordValue === password ? true : t`Passwords must be the same`;
              },
            },
          }}
          isDefaultMarginsOmitted={true}
          placeholder={t`Confirm password`}
          type={isPasswordShown ? "text" : "password"}
        />

        <div className={clsx("w-full flex items-center justify-between")}>
          <RegrelloFormFieldCheckbox
            isDefaultMarginsOmitted={true}
            label={t`Show password`}
            onChange={setIsPasswordShown}
            value={isPasswordShown}
          />
        </div>

        <RegrelloButton className="w-full" intent="primary" loading={isPending} type="submit">
          {t`Reset password`}
        </RegrelloButton>
      </form>
    </UnauthenticatedPageLayout>
  );
}
